/**
 * 博客列表页共享逻辑 — 封装 i18n 文案、分页数据获取、SEO 配置。
 * blog/index.vue 和 blog/page/[page].vue 共用此 composable 以消除重复。
 */
export async function useBlogListPage(pageNumber: number) {
  const { locale } = useI18n();
  const currentLocale = computed(() => (locale.value === 'en' ? 'en' : 'zh'));
  const PAGE_SIZE = 10;

  const { data } = await useFetch('/api/blog/list', {
    query: { locale: currentLocale, page: pageNumber, pageSize: PAGE_SIZE }
  });

  const items = computed(() => data.value?.items ?? []);
  const totalPages = computed(() => data.value?.totalPages ?? 1);
  const listForceNoIndex = computed(() => data.value?.forceNoIndex === true);

  const pageTitle = computed(() => (currentLocale.value === 'en' ? 'Blog' : '博客'));
  const pageSubtitle = computed(() =>
    currentLocale.value === 'en'
      ? 'Practical guides for PDF, Office, and subtitle translation workflows.'
      : '聚焦 PDF、Office 与字幕翻译的实操指南与检查清单。'
  );
  const emptyText = computed(() => (currentLocale.value === 'en' ? 'No published posts yet.' : '暂无已发布文章。'));
  const readMoreLabel = computed(() => (currentLocale.value === 'en' ? 'Read more' : '继续阅读'));

  const basePath = computed(() => (currentLocale.value === 'en' ? '/en/blog' : '/blog'));
  const prevLabel = computed(() => (currentLocale.value === 'en' ? 'Previous' : '上一页'));
  const nextLabel = computed(() => (currentLocale.value === 'en' ? 'Next' : '下一页'));
  const mobileLabel = computed(() =>
    currentLocale.value === 'en'
      ? `Page ${pageNumber} / ${totalPages.value}`
      : `第 ${pageNumber} / ${totalPages.value} 页`
  );

  const seoConfig = computed(() => ({
    title: () => (currentLocale.value === 'en' ? 'Blog | OLTranslation' : '博客 | OLTranslation'),
    description: () =>
      currentLocale.value === 'en'
        ? 'Expert translation guides and layout-preservation tips for PDF, Office documents, ebooks, and subtitle files. Published by the OLTranslation team.'
        : 'OLTranslation 官方博客，持续发布 PDF、Office 文档、电子书与字幕翻译的实操指南、格式处理技巧和行业检查清单，覆盖合同翻译、论文翻译、简历翻译和商务汇报 PPT 翻译等高频场景，帮助用户高效完成多语言文档处理。',
    forceNoIndex: listForceNoIndex.value,
    includeAlternates: true,
    pageNumber
  }));

  return {
    items,
    totalPages,
    listForceNoIndex,
    pageTitle,
    pageSubtitle,
    emptyText,
    readMoreLabel,
    basePath,
    prevLabel,
    nextLabel,
    mobileLabel,
    seoConfig
  };
}
